'use client';

import { type ReactNode, useEffect, useRef, useState } from 'react';
import { MessageSquare } from 'lucide-react';
import { useNewsStore } from '@/store/newsStore';
import { NewsCard } from '@/components/news';
import { CreateNewsModal, Toast } from '@/components/ui';
import { useLanguage } from '@/context/LanguageContext';

const EMPTY_POSTS: never[] = [];

interface GroupPostsTabProps {
  groupId?: number;
  groupDocumentId: string;
  groupName: string;
  canCreate?: boolean;
}

export function GroupPostsTab({
  groupId,
  groupDocumentId,
  groupName,
  canCreate = false,
}: GroupPostsTabProps) {
  const { t } = useLanguage();
  const { groupNewsByGroupDocumentId, fetchGroupNews, fetchCommentsBatch } = useNewsStore();
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const posts = groupNewsByGroupDocumentId[groupDocumentId] || EMPTY_POSTS;

  useEffect(() => {
    let cancelled = false;

    const loadPosts = async () => {
      setLoading(true);
      await fetchGroupNews(groupDocumentId);
      if (!cancelled) {
        setLoading(false);
      }
    };

    void loadPosts();

    return () => {
      cancelled = true;
    };
  }, [fetchGroupNews, groupDocumentId]);

  useEffect(() => {
    void fetchCommentsBatch(posts.map((post) => post.documentId));
  }, [posts, fetchCommentsBatch]);

  // Deep-link scroll + highlight + hash cleanup (MWBI-935). Post-comment and
  // group-activity emails link to /groups/<id>?tab=posts#post-<postDocumentId>.
  // Once posts load, scroll the named post into view, briefly highlight it, and
  // move keyboard/AT focus onto it, then strip the hash so a refresh or tab
  // switch does not re-trigger. Mirrors ForumTab's effect (the locked reference
  // for this pattern); the matching NewsCard self-expands its comments (child
  // effects run before this parent effect). Only the hash is cleared so the
  // ?tab=posts query keeping this tab active is preserved.
  //
  // Deliberate deviation from ForumTab, which keys on a topic COUNT: this keys
  // on the posts array itself, and retries until the target is actually in the
  // DOM. A refresh can swap in a different array of the same length (a post
  // added and another removed), and a count-keyed effect would never retry, so
  // the deep link would silently no-op. ForumTab shares that gap (MWBI-938).
  //
  // Re-running on every array identity change makes timer ownership subtle. The
  // scroll ATTEMPT is per-run and cancelled by that run's cleanup. The ring and
  // hash timers are NOT: once the highlight is on screen, removing it must
  // survive any re-render, or a store write landing inside the 3s window
  // cancels the un-highlight and the ring stays lit forever. They are tracked
  // in a ref and cleared only on unmount -- which still satisfies the MWBI-910
  // review point, since it is unmount (navigating away) that must stop the
  // delayed replaceState from stripping a later route's hash.
  const pendingTimers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const hasDeepLinked = useRef(false);

  useEffect(() => {
    const timers = pendingTimers.current;
    return () => timers.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || posts.length === 0) return;
    // The deep link fires at most once per mount; later renders must not
    // re-scroll or re-highlight a post the reader has already been shown.
    if (hasDeepLinked.current) return;
    const hash = window.location.hash;
    if (!hash.startsWith('#post-')) return;

    const scrollTimer = setTimeout(() => {
      const element = document.getElementById(hash.slice(1));
      // Not rendered yet -- leave the latch down so the next posts change retries.
      if (!(element instanceof HTMLElement)) return;

      hasDeepLinked.current = true;
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.focus({ preventScroll: true });
      element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');

      pendingTimers.current.push(
        setTimeout(() => {
          element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
        }, 3000),
        setTimeout(() => {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }, 500),
      );
    }, 500);
    pendingTimers.current.push(scrollTimer);

    return () => clearTimeout(scrollTimer);
  }, [posts]);

  const handleModalResult = (success: boolean, message: string) => {
    setToast({ message, type: success ? 'success' : 'error' });

    setTimeout(() => setToast(null), 3000);
  };

  let content: ReactNode;

  if (loading) {
    content = <div data-testid="group-posts-tab-loading-state" className="text-center py-12 text-foreground-muted">{t.groupTabs.loadingPosts}</div>;
  } else if (posts.length === 0) {
    content = (
      <div data-testid="group-posts-tab-empty-state" className="text-center py-12 text-foreground-muted border border-border rounded-lg bg-background-secondary">
        <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
        <p>{t.groupTabs.noPosts.replace('{groupName}', groupName)}</p>
        <p className="text-sm mt-1">{t.groupTabs.checkBackForPosts}</p>
      </div>
    );
  } else {
    content = (
      <div className="space-y-4">
        {posts.map((post) => (
          <NewsCard key={post.documentId} news={post} />
        ))}
      </div>
    );
  }

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {showCreateModal && (
        <CreateNewsModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onResult={handleModalResult}
          preselectedGroupId={groupId}
        />
      )}
      <div className="mx-auto max-w-4xl space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-foreground" data-testid="group-posts-tab-heading">{t.groupTabs.posts}</h2>
          {canCreate && groupId && (
            <button
              onClick={() => setShowCreateModal(true)}
              data-testid="group-posts-tab-create-post-btn"
              className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition"
            >
              {t.news.createPost}
            </button>
          )}
        </div>
        {content}
      </div>
    </>
  );
}
